import Papa from "papaparse"
import { useState, useEffect } from "react"
import { CeleryService, PolicyService, SettingsCommonService, UtilsService, type ApiError } from "./client"
import csvText from "./permissions.csv?raw"


export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: "Ungültige E-Mail Adresse",
}

export const passwordRules = (isRequired = true) => {
  const rules: any = {
    minLength: {
      value: 8,
      message: "Passwort muss mindestens 8 Zeichen lang sein",
    },
  }

  if (isRequired) {
    rules.required = "Passwort ist erforderlich"
  }

  return rules
}

export const confirmPasswordRules = (
  getValues: () => any,
  isRequired = true,
) => {
  const rules: any = {
    validate: (value: string) => {
      const password = getValues().password || getValues().new_password
      return value === password ? true : "Passwörter stimmen nicht überein"
    },
  }

  if (isRequired) {
    rules.required = "Passwortbestätigung ist erforderlich"
  }

  return rules
}

export const handleError = (err: ApiError, showToast: any) => {
  const errDetail = (err.body as any)?.detail
  let errorMessage = errDetail || "Etwas ist schief gelaufen."
  if (Array.isArray(errDetail) && errDetail.length > 0) {
    errorMessage = errDetail[0].msg
    /* Translate default SQL errors to german */
    if (errorMessage === "String should have at most 25 characters") {
      errorMessage = "Tag darf maximal 25 Zeichen lang sein"
    }
  }
  showToast("Fehler", errorMessage, "error")
}


export async function loadImageFile(path: string, name: string) {
  try {
    const response = await fetch(path)
    const blob = await response.blob()

    const file = new File([blob], name, { type: blob.type })

    return file
  } catch (error) {
    console.error("Failed to load image file:", error)
  }
}

type PermissionEntry = {
  label: string
  name: string
}

export const usePermissionsFromCSV = () => {
  const [permissions, setPermissions] = useState<PermissionEntry[]>([])

  useEffect(() => {
    const parsed = Papa.parse(csvText, {
      delimiter: ";",
      skipEmptyLines: true,
    })

    const data = parsed.data as string[][]

    const mapped = data.map(([label, name]) => ({
      label: label.trim(),
      name: name.trim(),
    }))

    setPermissions(mapped)
  }, [])

  return permissions
}

let cachedPermissions: string[] | null = null

export const userHasPermission = (items: any) => {
  if (!cachedPermissions || cachedPermissions.length == 0) {
    const permissionStr = localStorage.getItem("user_permissions")
    cachedPermissions = permissionStr ? JSON.parse(permissionStr) : []
  }

  return items.map((item: { perm: string; is_allowed: boolean }) => {
    item.is_allowed = item.perm ? cachedPermissions!.includes(item.perm) : false
    return item
  })
}

export const getSettingsCommon = () => {
  return {
    queryFn: async () => {
      const data = await SettingsCommonService.readSettingsCommon()
      return data ?? { data: null, count: 0 }
    },
    queryKey: ["settings-common"],
  }
}

export const getPolicyPublic = () => {
    return {
    queryFn: async () => {
      const data = await PolicyService.readPolicy()
      return data ?? { data: null, count: 0 }
    },
    queryKey: ["policy"],
  }
}

export const getModuleVersions = () => {
  return {
    queryFn: () => UtilsService.moduleVersions(),
    queryKey: ["module_versions"],
    staleTime: Infinity,
  }
}

export const waitForCelerySuccess = async (taskId: string, timeout: number = 200) => {
  while (true) {
    const data = await CeleryService.celeryTaskStatus({ taskId })

    if (data.status === "SUCCESS") {
      return true
    }

    if (data.status === "FAILURE") {
      return false
    }

    await new Promise((resolve) => setTimeout(resolve, timeout))
  }
}
